import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProgressBar from '../../components/ui/ProgressBar';
import { siteService } from '../../services/siteService';
import { documentService, normalizeRamsDocument } from '../../services/documentService';
import { ramsService } from '../../services/ramsService';
import { drawingService } from '../../services/drawingService';
import { incidentService } from '../../services/incidentService';
import { checklistService } from '../../services/checklistService';
import { auditService } from '../../services/auditService';
import { snagService } from '../../services/snagService';
import { dailyTaskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultDashboard } from '../../utils/roleAccess';
import { useLanguage } from '../../contexts/LanguageContext';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import OverviewTab from './components/OverviewTab';
import DocumentsTab from './components/DocumentsTab';
import DrawingsTab from './components/DrawingsTab';
import DailyTasksTab from './components/DailyTasksTab';
import IncidentsTab from './components/IncidentsTab';
import AuditsTab from './components/AuditsTab';
import SnagsTab from './components/SnagsTab';
import SiteActivityTimeline from './components/SiteActivityTimeline';
import ClientAccessSection from './components/ClientAccessSection';
import ClientVisibilitySection from './components/ClientVisibilitySection';
import PhotosTab from './components/PhotosTab';
import NotesTab from './components/NotesTab';

const SiteDetailDashboard = () => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [siteData, setSiteData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [audits, setAudits] = useState([]);
  const [snags, setSnags] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskCounts, setTaskCounts] = useState({ total: 0, completed: 0 });

  const breadcrumbItems = [
    { label: t('home'), href: getDefaultDashboard(userProfile?.role) },
    { label: t('Sites'), href: '/sites' },
    { label: siteData?.site_name || t('siteDetail') }
  ];

  useEffect(() => {
    if (siteId) {
      loadSiteData(siteId);
    } else {
      navigate('/sites', { replace: true });
    }
  }, [siteId, navigate]);

  useEffect(() => {
    // Handle deep-linking from notifications
    const tabParam = searchParams?.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const loadSiteData = async (id) => {
    try {
      setLoading(true);
      const siteResult = await siteService?.getSiteById(id);
      if (siteResult?.data) {
        setSiteData(siteResult?.data);

        // Hotfix (GIVolooT): pull H&S Docs from rams_documents alongside the
        // documents-table fetch so /rams-management uploads become visible
        // on the site detail Documents tab. ramsService needs both site_id
        // and company_id; the site row provides the latter.
        const siteCompanyId = siteResult?.data?.company_id;

        const [docsResult, ramsResult, drawingsResult, incidentsResult, checklistsResult, auditsResult, snagResult, taskCountsResult] = await Promise.all([
          documentService?.getDocumentsBySite(id),
          siteCompanyId
            ? ramsService?.getRamsForSite(id, siteCompanyId)
            : Promise.resolve({ data: [], error: null }),
          drawingService?.getLatestDrawings(id),
          incidentService?.getIncidentsBySite(id),
          checklistService?.getChecklistsBySite(id),
          auditService?.getAuditsBySite(id),
          snagService?.getSnagsBySite(id),
          dailyTaskService?.getCountsForSite(id)
        ]);

        // One source failing must not blank the whole tab — log and continue.
        if (ramsResult?.error) {
          console.warn('[SiteDetailDashboard] rams_documents fetch failed (non-fatal — documents-table rows still render):', ramsResult?.error?.message);
        }

        const docs = docsResult?.data || [];
        const ramsDocs = (ramsResult?.data || [])
          ?.map(normalizeRamsDocument)
          ?.filter(Boolean);
        const mergedDocs = [...docs, ...ramsDocs]
          ?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
        setDocuments(mergedDocs);
        if (drawingsResult?.data) setDrawings(drawingsResult?.data);
        if (incidentsResult?.data) setIncidents(incidentsResult?.data);
        if (checklistsResult?.data) setChecklists(checklistsResult?.data);
        if (auditsResult?.data) setAudits(auditsResult?.data);
        if (snagResult?.data) setSnags(snagResult?.data);
        if (taskCountsResult?.data) setTaskCounts(taskCountsResult?.data);
      } else {
        navigate('/sites', { replace: true });
      }
    } catch (err) {
      console.error('Error loading site data:', err);
      navigate('/sites', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!documents?.length) return 0;
    // Count all non-expired documents (valid, expiring-soon, expiring-critical)
    const nonExpiredDocs = documents?.filter(d => 
      d?.status === 'valid' || 
      d?.status === 'expiring-soon' || 
      d?.status === 'expiring-critical'
    )?.length;
    return Math.round((nonExpiredDocs / documents?.length) * 100);
  };

  const tabs = [
    { id: 'overview', label: t('Overview'), icon: 'LayoutDashboard', roles: ['admin', 'site_manager', 'worker', 'client'] },
    { id: 'documents', label: t('Documents'), icon: 'FileText', count: documents?.length, roles: ['admin', 'site_manager', 'worker', 'client'] },
    { id: 'drawings', label: t('Drawings'), icon: 'FileImage', count: drawings?.length, roles: ['admin', 'site_manager', 'worker', 'client'] },
    { id: 'daily-tasks', label: t('dailyTasks'), icon: 'CheckSquare', count: checklists?.length, roles: ['admin', 'site_manager', 'worker'] },
    { id: 'incidents', label: t('incidents'), icon: 'AlertTriangle', count: incidents?.length, roles: ['admin', 'site_manager', 'worker'] },
    { id: 'audits', label: t('auditsInspections'), icon: 'ClipboardCheck', count: audits?.length, roles: ['admin', 'site_manager', 'worker'] },
    { id: 'snags', label: t('Snags'), icon: 'ClipboardCheck', count: snags?.length, roles: ['admin', 'site_manager', 'worker'] },
    { id: 'photos', label: 'Photos', icon: 'Images', roles: ['admin', 'site_manager', 'worker', 'client'] },
    { id: 'notes', label: 'Notes', icon: 'MessageSquare', roles: ['admin', 'site_manager', 'worker'] },
    { id: 'timeline', label: t('activityTimeline'), icon: 'Activity', roles: ['admin', 'site_manager', 'worker', 'client'] },
    { id: 'client-visibility', label: 'Client Visibility', icon: 'Eye', roles: ['admin'] },
  ];

  const visibleTabs = tabs?.filter(tab => !tab?.roles || tab?.roles?.includes(userProfile?.role));
  const isReadOnly = isSiteReadOnlyStatus(siteData?.status);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <Icon name="Loader2" size={48} className="text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{siteData?.site_name || 'Site Detail'} - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowLeft"
              onClick={() => navigate('/sites')}
            >
              {t('back')}
            </Button>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <SiteReadOnlyBanner status={siteData?.status} siteId={siteData?.id} className="mb-6" />

          <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-lg flex-shrink-0">
                  <Icon name="Building2" size={32} color="var(--color-accent)" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    {siteData?.site_name}
                  </h1>
                  <p className="text-sm text-muted-foreground mb-1">
                    {siteData?.address_line1}, {siteData?.city}, {siteData?.postcode}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('client')}: {siteData?.client_name}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileBarChart"
                  onClick={() => navigate(`/daily-site-report?siteId=${siteId}`)}
                >
                  Daily Report
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  onClick={() => navigate('/compliance-report')}
                >
                  {t('report')}
                </Button>
                {userProfile?.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Settings"
                    onClick={() => navigate(`/sites/${siteId}/settings`)}
                  >
                    {t('settings')}
                  </Button>
                )}
                {userProfile?.role === 'admin' && (
                  <Button
                    variant="primary"
                    size="sm"
                    iconName="Eye"
                    onClick={() => setActiveTab('client-visibility')}
                  >
                    Client Visibility
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('status')}</p>
                <p className="text-sm font-medium text-foreground capitalize">{siteData?.status?.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('startDate')}</p>
                <p className="text-sm font-medium text-foreground">{new Date(siteData?.start_date)?.toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('endDate')}</p>
                <p className="text-sm font-medium text-foreground">{new Date(siteData?.end_date)?.toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('workers')}</p>
                <p className="text-sm font-medium text-foreground">{siteData?.worker_count || 0}</p>
              </div>
            </div>

            <div>
              <ProgressBar
                value={calculateProgress()}
                label={t('overallProgress')}
                height="h-2"
                color="bg-accent"
              />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-elevation-2">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex items-center">
                {visibleTabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium transition-smooth whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab?.id
                        ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={20} />
                    <span>{tab?.label}</span>
                    {tab?.count !== undefined && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        activeTab === tab?.id
                          ? 'bg-accent text-accent-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 md:p-6">
              {activeTab === 'overview' && (
                <OverviewTab
                  siteData={siteData}
                  documents={documents}
                  incidents={incidents}
                  audits={audits}
                  snags={snags}
                  tasks={tasks}
                  taskCounts={taskCounts}
                  checklists={checklists}
                  userRole={userProfile?.role}
                  onRefresh={loadSiteData}
                  onTabChange={setActiveTab}
                />
              )}
              {activeTab === 'documents' && (
                <DocumentsTab
                  siteId={siteId}
                  documents={documents}
                  onRefresh={() => loadSiteData(siteId)}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'drawings' && (
                <DrawingsTab
                  siteId={siteId}
                  siteData={siteData}
                  drawings={drawings}
                  onRefresh={() => loadSiteData(siteId)}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'daily-tasks' && (
                <DailyTasksTab
                  siteId={siteId}
                  checklists={checklists}
                  onRefresh={() => loadSiteData(siteId)}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'incidents' && (
                <IncidentsTab
                  siteId={siteId}
                  siteData={siteData}
                  incidents={incidents}
                  onRefresh={() => loadSiteData(siteId)}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'audits' && (
                <AuditsTab
                  siteId={siteId}
                  siteData={siteData}
                  audits={audits}
                  onRefresh={() => loadSiteData(siteId)}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'snags' && (
                <SnagsTab
                  siteId={siteId}
                  snags={snags}
                  onRefresh={() => loadSiteData(siteId)}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'photos' && (
                <PhotosTab
                  siteId={siteId}
                  snags={snags}
                  incidents={incidents}
                />
              )}
              {activeTab === 'notes' && (
                <NotesTab
                  siteId={siteId}
                  companyId={siteData?.company_id}
                />
              )}
              {activeTab === 'timeline' && (
                <SiteActivityTimeline
                  siteId={siteId}
                />
              )}
              {activeTab === 'client-access' && (
                <ClientAccessSection
                  siteId={siteId}
                  siteData={siteData}
                />
              )}
              {activeTab === 'client-visibility' && (
                <ClientVisibilitySection
                  siteId={siteId}
                />
              )}
            </div>
          </div>
        </main>

      </div>
    </>
  );
};

export default SiteDetailDashboard;